/**
 * Kiosk Preview Store
 * Manages form factor state for the live preview panel
 * Derives preview config from kiosk-settings-store
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { KioskUICustomization } from '@/features/device-customizer/kiosk/types/kiosk-types';
import { mapSettingsToPreviewConfig } from '../utils/kiosk-config-mapper';
import { useKioskSettingsStore } from './kiosk-settings-store';

interface KioskPreviewState {
  /** Current form factor for preview */
  formFactor: '4inch' | '5inch';

  /** Set form factor */
  setFormFactor: (factor: '4inch' | '5inch') => void;

  /** Get live preview config (derives from kiosk-settings-store) */
  getPreviewConfig: () => KioskUICustomization;
}

export const useKioskPreviewStore = create<KioskPreviewState>()(
  persist(
    (set, get) => ({
      // Default to 5"
      formFactor: '5inch',

      // Set form factor
      setFormFactor: (factor) => {
        set({ formFactor: factor });
      },

      // Derive preview config from settings store
      getPreviewConfig: () => {
        const { formFactor } = get();
        const settings = useKioskSettingsStore.getState().settings;
        return mapSettingsToPreviewConfig(settings, formFactor);
      },
    }),
    {
      name: 'kiosk-preview-storage',
      version: 1, // Increment to force migration and reset to new defaults
      partialize: (state) => ({
        formFactor: state.formFactor, // Only persist form factor preference
      }),
    }
  )
);
